import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faSpinner,
  faExclamationTriangle,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import { powerStatsAPI, CURRENT_SEASON, AVAILABLE_SEASONS } from '../../Constants';
import { AuthContext } from '../../context/AuthContext';
import '../../styles/admin/admin-match-editor.scss';

// Define interfaces
interface Team {
  id: string;
  name: string;
  logo: string | null;
  division?: string | null;
  seed?: string | null;
  players: any[];
}

interface UpcomingMatch {
  challongeId: string;
  team1: Team | null;
  team2: Team | null;
  isPlayoff: boolean;
  playoffPlayed: boolean;
  division: string;
  matchTime: string;
  streamUrl: string;
  veto?: string | null;
  bracketPosition?: number | null;
  season?: number; // Add season field
  hidden?: boolean; // Add hidden field
}

// Define API response interface to match actual backend response
interface TeamApiResponse {
  teamId: string;
  teamName: string;
  teamLogo: string;
  ownerId: string;
  season: number;
  division: string;
  twitter: string | null;
  coachID: string | null;
  website: string | null;
  seed: string;
  playoffSeed: string | null;
}

// Define the API's expected team format based on backend model
interface ApiTeam {
  TeamId: string;
  TeamName: string;
  TeamLogo: string;
  OwnerId: string;
  Season: number;
  Division: string;
  Twitter?: string | null;
  CoachID?: string | null;
  Website?: string | null;
  Seed: string;
  PlayoffSeed?: string | null;
}

// Define the API's expected match format based on backend model
interface ApiMatch {
  challonge_id: string;
  team1: ApiTeam;
  team2: ApiTeam;
  isPlayoff: boolean;
  playoffPlayed: boolean;
  division: string;
  match_time: string;
  stream_url: string;
  Veto?: string | null;
  roundNumber?: number | null;
  season: number;
  hidden: boolean;
}

const AdminMatchEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { challongeId } = useParams<{ challongeId: string }>();
  const isEditMode = !!challongeId;
  const { token, isAdmin } = useContext(AuthContext);
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";
  const [formData, setFormData] = useState<UpcomingMatch>({
    challongeId: '',
    team1: null,
    team2: null,
    isPlayoff: false,
    playoffPlayed: false,
    division: 'Main',
    matchTime: new Date().toISOString().slice(0, 16), // Format: YYYY-MM-DDTHH:MM
    streamUrl: '',
    veto: '',
    bracketPosition: null,
    season: CURRENT_SEASON, // Default to current season
    hidden: false // Default to not hidden
  });
  
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(CURRENT_SEASON); // Default to current season
  
  // Check if user is an admin before allowing access
  useEffect(() => {
    if (!token || !isAdmin) {
      navigate('/admin/login');
    }
  }, [token, isAdmin, navigate]);
  
  // Map API response to Team interface
  const mapApiTeamToTeam = (apiTeam: TeamApiResponse): Team => {
    return {
      id: apiTeam.teamId,
      name: apiTeam.teamName,
      logo: apiTeam.teamLogo,
      division: apiTeam.division,
      seed: apiTeam.seed,
      players: [] // Assuming players aren't included in the API response
    };
  };
  
  // Map Team interface to API Team format
  const mapTeamToApiTeam = (team: Team, season: number): ApiTeam => {
    return {
      TeamId: team.id,
      TeamName: team.name,
      TeamLogo: team.logo || "",
      OwnerId: "", // This may need to be fetched or stored somewhere
      Season: season,
      Division: team.division || "",
      Seed: team.seed || "1",
      Twitter: null,
      CoachID: null,
      Website: null,
      PlayoffSeed: null
    };
  };
  
  // Fetch teams for the selected season
  const fetchTeams = async (season: number) => {
    try {
      const url = `${powerStatsAPI}/Team/getTeamsBySeason/${season}`;
      
      const teamsResponse = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!teamsResponse.ok) {
        throw new Error(`Error fetching teams: ${teamsResponse.status} ${teamsResponse.statusText}`);
      }
      
      const rawTeamsData = await teamsResponse.json();
      
      // Transform the API response to match the Team interface
      const teamsData = rawTeamsData.map((team: TeamApiResponse) => mapApiTeamToTeam(team));
      setTeams(teamsData);
      
      // If editing and we don't have a team1/team2 selected yet, try to find them in the new teams list
      if (isEditMode && formData.team1?.id && formData.team2?.id) {
        const team1 = teamsData.find((t: Team) => t.id === formData.team1?.id);
        const team2 = teamsData.find((t: Team) => t.id === formData.team2?.id);
        
        // Only update if teams change
        if (
          (team1 && JSON.stringify(team1) !== JSON.stringify(formData.team1)) ||
          (team2 && JSON.stringify(team2) !== JSON.stringify(formData.team2))
        ) {
          setFormData(prev => ({
            ...prev,
            team1: team1 || prev.team1,
            team2: team2 || prev.team2
          }));
        }
      }
      
      return teamsData;
    } catch (err) {
      console.error('Error loading teams:', err);
      setError(`Failed to load teams for season ${season}. Please try again later.`);
      return null;
    }
  };
  
const fetchMatchData = async () => {
  try {
    if (!isEditMode || !challongeId) return null;
    
    
    // Updated endpoint to get a specific match by its challongeId
    const matchResponse = await fetch(`${powerStatsAPI}/Match/getUpcomingMatchById/${challongeId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!matchResponse.ok) {
      throw new Error(`Error fetching match: ${matchResponse.status} ${matchResponse.statusText}`);
    }
    
    // Parse the response
    const matchData = await matchResponse.json();
    
    // Check if match has a season, default to 2 if not
    const season = matchData.season || 2;
    setSelectedSeason(season);
    
    // Convert match time to local date-time format for input
    const matchTime = new Date(matchData.match_time).toISOString().slice(0, 16);
    
    // Transform teams directly from the match data - USING CORRECT PROPERTY NAMES
    const team1 = matchData.team1 ? {
      id: matchData.team1.teamId,        // camelCase - corrected from TeamId
      name: matchData.team1.teamName,    // camelCase - corrected from TeamName
      logo: matchData.team1.teamLogo,    // camelCase - corrected from TeamLogo
      division: matchData.team1.division, // camelCase - corrected from Division
      seed: matchData.team1.seed,        // camelCase - corrected from Seed
      players: []
    } : null;
    
    const team2 = matchData.team2 ? {
      id: matchData.team2.teamId,        // camelCase - corrected from TeamId
      name: matchData.team2.teamName,    // camelCase - corrected from TeamName
      logo: matchData.team2.teamLogo,    // camelCase - corrected from TeamLogo
      division: matchData.team2.division, // camelCase - corrected from Division 
      seed: matchData.team2.seed,        // camelCase - corrected from Seed
      players: []
    } : null;
    
    
    // Make sure the teams from the match are added to the teams list
    if (team1) {
      const existingTeamIndex = teams.findIndex(t => t.id === team1.id);
      if (existingTeamIndex === -1) {
        setTeams(prev => [...prev, team1]);
      }
    }
    
    if (team2) {
      const existingTeamIndex = teams.findIndex(t => t.id === team2.id);
      if (existingTeamIndex === -1) {
        setTeams(prev => [...prev, team2]);
      }
    }
    
    const transformedMatchData: UpcomingMatch = {
      challongeId: matchData.challonge_id,
      team1,
      team2,
      isPlayoff: matchData.isPlayoff,
      playoffPlayed: matchData.playoffPlayed,
      division: matchData.division,
      matchTime: matchTime,
      streamUrl: matchData.stream_url,
      veto: matchData.Veto,
      bracketPosition: matchData.roundNumber,
      season: season,
      hidden: matchData.hidden || false
    };
    
    return transformedMatchData;
  } catch (err) {
    console.error('Error loading match data:', err);
    setError('Failed to load match data. Please try again later.');
    return null;
  }
};
  
  // Fetch teams and match data
  useEffect(() => {
// In the useEffect for loading data
// In the useEffect for loading data
const loadData = async () => {
  setLoading(true);
  setError(null);
  
  try {
    let matchData: UpcomingMatch | null = null;
    let season = selectedSeason;
    
    // First fetch match data if in edit mode to get the correct season
    if (isEditMode) {
      matchData = await fetchMatchData();
      
      if (matchData && matchData.season) {
        season = matchData.season;
        setSelectedSeason(season);
      }
    }
    
    // Then fetch teams for that season
    const teamsData = await fetchTeams(season);
    
    // If in edit mode and we have match data, update the form
    if (isEditMode && matchData) {
      
      // Create a non-null variable for matchData within this block
      const safeMatchData = matchData;
      
      // Check if teams from match are in the teams list
      if (safeMatchData.team1) {
        const team1 = safeMatchData.team1; // Create a local reference to team1
        const team1Found = teamsData?.some((t: Team) => t.id === team1.id);
        
        if (!team1Found && teamsData) {
          // Use the local reference which TypeScript knows is not null
          setTeams([...teamsData, team1]);
        }
      }
      
      if (safeMatchData.team2) {
        const team2 = safeMatchData.team2; // Create a local reference to team2
        const team2Found = teamsData?.some((t: Team) => t.id === team2.id);
        
        if (!team2Found && teamsData) {
          // Use the local reference which TypeScript knows is not null
          setTeams(prev => [...prev, team2]);
        }
      }
      
      setFormData(safeMatchData);
      
      // Check what's in the forms after setting
      setTimeout(() => {
      }, 100);
    } else {
      // For new matches, just set the season
      setFormData(prev => ({
        ...prev,
        season
      }));
    }
  } catch (err) {
    console.error('Error loading data:', err);
    setError('Failed to load data. Please try again later.');
  } finally {
    setLoading(false);
  }
};
    
    loadData();
    
    // Set page title
    document.title = isEditMode 
      ? 'Edit Match - Northern Championships Series Admin' 
      : 'Create Match - Northern Championships Series Admin';
  }, [isEditMode, challongeId, token]);
  
  // Handle season change
  const handleSeasonChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSeason = parseInt(e.target.value);
    
    setSelectedSeason(newSeason);
    
    // Update form data with the new season
    setFormData(prev => ({
      ...prev,
      season: newSeason,
      // Clear team selections when changing season
      team1: null,
      team2: null
    }));
    
    // Reload teams for the new season
    await fetchTeams(newSeason);
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (name === 'isPlayoff' || name === 'playoffPlayed' || name === 'hidden') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'team1Id') {
      const selectedTeam = teams.find(team => team.id === value);
      setFormData(prev => ({
        ...prev,
        team1: selectedTeam || null
      }));
    } else if (name === 'team2Id') {
      const selectedTeam = teams.find(team => team.id === value);
      setFormData(prev => ({
        ...prev,
        team2: selectedTeam || null
      }));
    } else if (name === 'bracketPosition') {
      const numberValue = value === '' ? null : parseInt(value);
      setFormData(prev => ({
        ...prev,
        bracketPosition: numberValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form - validation code remains the same
    if (!formData.challongeId) {
      setError('Challonge ID is required');
      return;
    }
    
    if (!formData.team1 || !formData.team2) {
      setError('Both teams must be selected');
      return;
    }
    
    
    if (!formData.matchTime) {
      setError('Match time is required');
      return;
    }
    
    if (!formData.season) {
      setError('Season is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Make sure season is a number
      const season = formData.season ?? 2; // Use season if defined, otherwise default to 2
      
      // Transform team objects to match API expectations with PascalCase property names
      const apiTeam1 = mapTeamToApiTeam(formData.team1, season);
      const apiTeam2 = mapTeamToApiTeam(formData.team2, season);
      
      // Create API match object with snake_case property names where needed
      const apiMatch: ApiMatch = {
        challonge_id: formData.challongeId,
        team1: apiTeam1,
        team2: apiTeam2,
        isPlayoff: formData.isPlayoff,
        playoffPlayed: formData.playoffPlayed,
        division: formData.division,
        // Convert matchTime to ISO string format
        match_time: new Date(formData.matchTime).toISOString(),
        stream_url: formData.streamUrl || "",
        Veto: formData.veto || null,
        roundNumber: formData.bracketPosition, // Map bracketPosition to roundNumber
        season: season, // Ensure this is using the selected season
        hidden: formData.hidden || false // Include hidden field, default to false
      };
      
      
      const url = isEditMode 
        ? `${powerStatsAPI}/Match/updateUpcomingMatch` 
        : `${powerStatsAPI}/Match/createUpcomingMatch`;
      
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiMatch) // Send the match directly without wrapping it
      });
      
      if (!response.ok) {
        // Try to get more details about the error
        let errorData;
        try {
          errorData = await response.json();
          console.error('API Error response:', errorData);
          
          // Format validation errors if present
          if (errorData.errors) {
            const errorMessages = Object.entries(errorData.errors)
              .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
              .join('; ');
            throw new Error(`Validation errors: ${errorMessages}`);
          }
        } catch (jsonError) {
          // If we can't parse the error as JSON, use the status text
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        
        throw new Error(`Error: ${errorData.title || response.statusText}`);
      }
      
      
      // Redirect to tournaments page
      navigate('/admin/tournaments');
    } catch (err) {
      console.error('Error saving match:', err);
      setError(`Failed to save match: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="admin-match-editor loading">
        <div className="container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-match-editor">
      <div className="container">
        <div className="editor-header">
          <button 
            type="button"
            className="back-button"
            onClick={() => navigate('/admin/tournaments')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Tournaments
          </button>
          <h1>{isEditMode ? 'Edit Match' : 'Create New Match'}</h1>
        </div>
        
        {error && (
          <div className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-main">
              <div className="form-group">
                <label htmlFor="challongeId">Challonge ID</label>
                <input
                  type="text"
                  id="challongeId"
                  name="challongeId"
                  value={formData.challongeId}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter Challonge ID"
                  disabled={isEditMode} // Can't edit ID in edit mode
                />
                <div className="help-text">
                  Unique identifier for the match from Challonge
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="season">Season</label>
                <select
                  id="season"
                  name="season"
                  value={selectedSeason}
                  onChange={handleSeasonChange}
                  required
                  disabled={isEditMode} // Can't change season in edit mode
                >
                  {AVAILABLE_SEASONS.map((s) => (
                    <option key={s} value={s}>
                      Season {s}
                    </option>
                  ))}
                </select>
                <div className="help-text">
                  {isEditMode ? 'Season cannot be changed for existing matches' : 'Select the season for this match'}
                </div>
              </div>
              
              <div className="form-section">
                <h3>Teams</h3>
                
                <div className="form-group">
                  <label htmlFor="team1Id">Team 1</label>
                  <select
                    id="team1Id"
                    name="team1Id"
                    value={formData.team1?.id || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Team 1</option>
                    {teams.map(team => (
                      <option key={`team1-${team.id}`} value={team.id}>
                        {team.name} {team.division ? `(${team.division})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="team2Id">Team 2</label>
                  <select
                    id="team2Id"
                    name="team2Id"
                    value={formData.team2?.id || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Team 2</option>
                    {teams.map(team => (
                      <option key={`team2-${team.id}`} value={team.id}>
                        {team.name} {team.division ? `(${team.division})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-section">
                <h3>Match Details</h3>
                
                <div className="form-group">
                  <label htmlFor="division">Division</label>
                  <input
                    type="text"
                    id="division"
                    name="division"
                    value={formData.division}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter division"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="matchTime">Match Date & Time</label>
                  <input
                    type="datetime-local"
                    id="matchTime"
                    name="matchTime"
                    value={formData.matchTime}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="streamUrl">Stream URL</label>
                  <input
                    type="url"
                    id="streamUrl"
                    name="streamUrl"
                    value={formData.streamUrl || ''}
                    onChange={handleInputChange}
                    placeholder="Enter stream URL (optional)"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="bracketPosition">Bracket Position</label>
                  <input
                    type="number"
                    id="bracketPosition"
                    name="bracketPosition"
                    value={formData.bracketPosition === null ? '' : formData.bracketPosition}
                    onChange={handleInputChange}
                    placeholder="Enter bracket position (optional)"
                  />
                  <div className="help-text">
                    Position in tournament bracket (if applicable)
                  </div>
                </div>
                
                <div className="form-group checkbox-group">
                  <label htmlFor="isPlayoff" className="checkbox-label">
                    <input
                      type="checkbox"
                      id="isPlayoff"
                      name="isPlayoff"
                      checked={formData.isPlayoff}
                      onChange={handleInputChange}
                    />
                    <span>Playoff Match</span>
                  </label>
                </div>
                
                <div className="form-group checkbox-group">
                  <label htmlFor="playoffPlayed" className="checkbox-label">
                    <input
                      type="checkbox"
                      id="playoffPlayed"
                      name="playoffPlayed"
                      checked={formData.playoffPlayed}
                      onChange={handleInputChange}
                    />
                    <span>Playoff Played</span>
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label htmlFor="hidden" className="checkbox-label">
                    <input
                      type="checkbox"
                      id="hidden"
                      name="hidden"
                      checked={formData.hidden || false}
                      onChange={handleInputChange}
                    />
                    <span>Hidden Match (won't be displayed publicly)</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="veto">Veto Information</label>
                  <textarea
                    id="veto"
                    name="veto"
                    value={formData.veto || ''}
                    onChange={handleInputChange}
                    placeholder="Enter veto information (optional)"
                    rows={4}
                  />
                </div>
              </div>
            </div>
            
            <div className="form-sidebar">
              <div className="sidebar-section actions">
                <div className="action-buttons">
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={() => navigate('/admin/tournaments')}
                  >
                    CANCEL
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> SAVING...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> {isEditMode ? 'UPDATE MATCH' : 'CREATE MATCH'}
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {(formData.team1 && formData.team2) && (
                <div className="sidebar-section match-preview">
                  <h3>Match Preview</h3>
                  <div className="match-card">
                    <div className="match-teams">
                      <div className="team">
                        <img 
                          src={formData.team1.logo || teamPlaceholderImage} 
                          alt={`${formData.team1.name} logo`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span>{formData.team1.name}</span>
                      </div>
                      <div className="vs">VS</div>
                      <div className="team">
                        <img 
                          src={formData.team2.logo || teamPlaceholderImage} 
                          alt={`${formData.team2.name} logo`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = teamPlaceholderImage;
                          }}
                        />
                        <span>{formData.team2.name}</span>
                      </div>
                    </div>
                    <div className="match-details">
                      <div className="match-time">
                        {new Date(formData.matchTime).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="match-division">
                        {formData.division}
                      </div>
                      <div className="match-type">
                        {formData.isPlayoff ? 'Playoff' : 'Regular'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMatchEditorPage;